import { isEmpty } from "lodash";
import { FC, useEffect, useMemo, useRef } from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLineOfBusiness } from "../../../../api/services/programTemplate/programTemplateApis";
import { useGetUsersDetailByRoleMutation } from "../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiSelectWithTextInput,
  SearchSaveFiltersSubHeader,
} from "../../../../components";
import {
  LANGUAGE_CODE,
  MAX_LENGTH_100,
  MAX_LENGTH_50,
  ROLE_IDS,
} from "../../../../helpers/constants";
import { UserDetails } from "../../../../helpers/types/common";
import {
  mapDropdownDataWithName,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { appStateSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  ProjectSearchFormKey,
  ProjectSearchFormProps,
} from "../../modelsAndSchemas/Models";
import { searchSectionSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ProjectSearchFormWrapper } from "./ProjectSearchForm.style";

interface ProjectSearchProps extends FieldValues {
  control: Control<ProjectSearchFormProps>;
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  handleSaveFilter: () => void;
  disableActionBtn: boolean;
  anchorRef: React.RefObject<HTMLButtonElement>;
  showAsModal?: boolean;
  isValidForm?: boolean;
}

const pageId = "project-search";

export const ProjectSearchForm: FC<ProjectSearchProps> = ({
  control,
  handleClearAllSearch,
  handleSearchClick,
  handleSaveFilter,
  handleSavedFilter,
  disableActionBtn,
  anchorRef,
  showAsModal,
  isValidForm,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { fetchLabel, commonLabels, fetchComponentTitle, fetchDropdownValues, commonValidationMessages } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const dispatch = useDispatch();
  const { preferredLanguage } = useSelector(appStateSelector);

  const {
    data: lineOfBusinesses,
    isFetching: businessPending,
    isError: lobError,
  } = useGetLineOfBusiness(preferredLanguage ?? LANGUAGE_CODE.EN_US);

  const productLineFieldRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const projectManagerFieldRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const [productLines, projectManager] = useWatch({
    control,
    name: [
      ProjectSearchFormKey.productLines,
      ProjectSearchFormKey.productManagers,
    ],
  });

  useEffect(() => {
    productLineFieldRef.current?.resetValues(productLines);
    projectManagerFieldRef.current?.resetValues(projectManager);
  }, [productLines, projectManager]);

  const {
    data: projectManagerList,
    isPending: isManagerPending,
    isError: isManagerError,
    mutateAsync: getProjectManagerData,
  } = useGetUsersDetailByRoleMutation();

  const filterDropdownValue = fetchDropdownValues(
    searchSectionSiteCoreKeys.header,
    searchSectionSiteCoreKeys.filterByList,
  );

  const projectStatusDropdownValue = fetchDropdownValues(
    searchSectionSiteCoreKeys.header,
    searchSectionSiteCoreKeys.projectStatusList,
  );

  const isError = lobError || isManagerError;

  const isLoading = businessPending || isManagerPending;

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    getProjectManagerData({ roleIds: [ROLE_IDS.projectManager] });
  }, []);

  const productLineDropdownValue = useMemo(() => {
    if (isEmpty(lineOfBusinesses)) {
      return [];
    }

    return setApiDataToDropDownOption(lineOfBusinesses);
  }, [lineOfBusinesses]);

  const productManagerDropdownValue = useMemo(() => {
    if (isEmpty(projectManagerList)) {
      return [];
    }

    return mapDropdownDataWithName(projectManagerList as UserDetails[]);
  }, [projectManagerList]);

  const onClearAll = () => {
    handleClearAllSearch();
    productLineFieldRef.current?.resetValues();
    projectManagerFieldRef.current?.resetValues();
  };

  const renderProjectSearchFormHeader = () => {
    if (showAsModal) {
      return null;
    }
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={handleSavedFilter}
        onResetAllClick={onClearAll}
        isSavedFilterDisabled={disableActionBtn}
        anchorRef={anchorRef}
        resetButtonTestId={`${pageId}-clearAll`}
        savedFilterTestId={`${pageId}-savedFilter`}
      />
    );
  };

  return (
    <ProjectSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={!showAsModal}
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(searchSectionSiteCoreKeys.header)}
          />
        }
      >
        {renderProjectSearchFormHeader()}
        <form data-testid="search-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.projectName}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.projectName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ProjectSearchFormKey.projectNameFilter}
                  id={`${pageId}-${ProjectSearchFormKey.projectName}`}
                  selectBoxId={`${pageId}-${ProjectSearchFormKey.projectNameFilter}`}
                  maxLength={MAX_LENGTH_100}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.projectKey}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.projectKey,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ProjectSearchFormKey.projectKeyFilter}
                  id={`${pageId}-${ProjectSearchFormKey.projectKey}`}
                  selectBoxId={`${pageId}-${ProjectSearchFormKey.projectKeyFilter}`}
                  maxLength={MAX_LENGTH_50}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.clientName}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.clientName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ProjectSearchFormKey.clientNameFilter}
                  id={`${pageId}-${ProjectSearchFormKey.clientName}`}
                  selectBoxId={`${pageId}-${ProjectSearchFormKey.clientNameFilter}`}
                  maxLength={MAX_LENGTH_50}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.clientCode}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.clientCode,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ProjectSearchFormKey.clientCodeFilter}
                  id={`${pageId}-${ProjectSearchFormKey.clientCode}`}
                  selectBoxId={`${pageId}-${ProjectSearchFormKey.clientCodeFilter}`}
                  maxLength={MAX_LENGTH_100}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.productLines}
                ref={productLineFieldRef}
              >
                <MuiMultiSelect
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.productLineDropDown,
                  )}
                  value=""
                  showSearch
                  showChip
                  chipId={`${pageId}-${ProjectSearchFormKey.productLines}-chip`}
                  dialogTitle={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.productLineDropDown,
                  )}
                  options={productLineDropdownValue}
                  id={`${pageId}-${ProjectSearchFormKey.productLines}`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.productManagers}
                ref={projectManagerFieldRef}
              >
                <MuiMultiSelect
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.projectManagerDropDown,
                  )}
                  value=""
                  showSearch
                  showChip
                  chipId={`${pageId}-${ProjectSearchFormKey.productManagers}-chip`}
                  dialogTitle={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.projectManagerDropDown,
                  )}
                  options={productManagerDropdownValue}
                  id={`${pageId}-${ProjectSearchFormKey.productManagers}`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.projectStatus}
              >
                <MuiSelect
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.projectStatusDropDown,
                  )}
                  options={projectStatusDropdownValue}
                  value=""
                  id={`${pageId}-${ProjectSearchFormKey.projectStatus}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProjectSearchFormKey.participantName}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    searchSectionSiteCoreKeys.header,
                    searchSectionSiteCoreKeys.participantName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ProjectSearchFormKey.participantNameFilter}
                  id={`${pageId}-${ProjectSearchFormKey.participantName}`}
                  selectBoxId={`${pageId}-${ProjectSearchFormKey.participantNameFilter}`}
                  maxLength={MAX_LENGTH_50}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid
            container
            className={`button-container ${showAsModal && "padding-horizontal"}`}
          >
            <MuiButtonSecondary
              onClick={handleSaveFilter}
              id={`${pageId}-saveFilter`}
              data-testid="save-filter"
              disabled={!isValidForm}
            >
              {commonLabels.saveFilter}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
              data-testid="search"
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </ProjectSearchFormWrapper>
  );
};
